import React from 'react';
import {
  Navigate,
  SentryRoutes,
  Route,
  AppendNavigate,
  NavigateParsedParams,
  NavigateWildCard,
} from '@common/components';
import { PrivateRoute } from '@routes/private-route';
import { FourZeroFourPage } from '@identity/pages';
import { SupplierStaffingRequestsPage } from '@applicant-flow/pages/supplier/supplier-staffing-requests-page';
import { SupplierStaffingRequestPage } from 'works/legacy/pages/supplier/staffing-requests/staffing-request';
import { SupplierCandidatesPage } from 'works/legacy/pages/supplier/candidates';
import { SupplierCandidatePage } from 'works/legacy/pages/supplier/candidates/candidate';
import { SupplierTeamPage } from '@working-experience/pages/supplier';
import { BillingPage, ReverseInvoicePage } from '@pay-bill/pages/supplier';
import { AccountSettingsPage } from '@identity/pages/account-settings-page';
import { ReportsPage } from 'works/legacy/pages/organization/reports';
import { SupplierCompliancePage } from '@onboarding/pages/supplier/supplier-compliance-page';
import { SupplierAdminSettingsRoutes } from '@routes/supplier/supplier-admin-settings-routes';
import { HomePage } from '@institutions/pages/home-page';
import { CandidateAssignmentPage } from 'works/legacy/components/candidate-assignment';
import {
  TimesheetsPage,
  WorkerTimesheetPage,
} from '@timekeeping/pages/supplier';

export function SupplierRoutes() {
  return (
    <SentryRoutes>
      {/* ADMIN SETTINGS */}

      <Route
        path="/works/supplier/admin/*"
        element={<SupplierAdminSettingsRoutes />}
      />

      {/* HOME */}

      <Route path="/works" element={<Navigate to="/works/supplier" />} />

      <Route
        path="/works/supplier"
        element={<PrivateRoute page={HomePage} menuItem="home" />}
      />

      {/* CANDIDATES */}

      <Route
        path="/works/supplier/candidates/:worksProfileId"
        element={<AppendNavigate append="submissions" />}
      />
      <Route
        path="/works/supplier/candidates/:worksProfileId/:tabKey" // tabKey: submissions | profile | reverse_invoices
        element={
          <PrivateRoute page={SupplierCandidatePage} menuItem="candidates" />
        }
      />

      <Route
        path="/works/supplier/candidates"
        element={
          <PrivateRoute page={SupplierCandidatesPage} menuItem="candidates" />
        }
      />

      {/* COMPLIANCE */}

      <Route
        path="/works/supplier/compliance"
        element={<AppendNavigate append="expired" />}
      />
      <Route
        path="/works/supplier/compliance/:tabKey" // tabKey: expiring | expired
        element={
          <PrivateRoute page={SupplierCompliancePage} menuItem="compliance" />
        }
      />

      {/* TEAM / ASSIGNMENTS */}

      <Route
        path="/works/supplier/team"
        element={<PrivateRoute page={SupplierTeamPage} menuItem="team" />}
      />

      <Route
        path="/works/supplier/team/:id"
        element={<AppendNavigate append="assignment" />}
      />
      <Route
        path="/works/supplier/team/:id/:tabKey" // tabKey: assignment | compliance | info | profile | notes
        element={
          <PrivateRoute page={CandidateAssignmentPage} menuItem="team" />
        }
      />

      {/* STAFFING REQUESTS */}

      <Route
        path="/works/supplier/staffing-requests"
        element={<AppendNavigate append="requests" />}
      />
      <Route
        path="/works/supplier/staffing-requests/:tab" // tab: requests | submissions | offers | accepted | extensions
        element={
          <PrivateRoute
            page={SupplierStaffingRequestsPage}
            menuItem="requests"
          />
        }
      />

      <Route
        path="/works/supplier/staffing-request/:id"
        element={<AppendNavigate append="submitted" />}
      />
      <Route
        path="/works/supplier/staffing-request/:id/:tab" // tab: not_submitted | submitted | offers | accepted | withdrawn | rejected
        element={
          <PrivateRoute
            page={SupplierStaffingRequestPage}
            menuItem="requests"
          />
        }
      />

      {/* BILLING */}

      <Route
        path="/works/supplier/billing/reverse-invoices/:id"
        element={<AppendNavigate append="summary" />}
      />
      <Route
        path="/works/supplier/billing/reverse-invoices/:id/:tabKey" // tabKey: summary | hours | disputes | history
        element={<PrivateRoute page={ReverseInvoicePage} menuItem="billing" />}
      />

      <Route
        path="/works/supplier/billing"
        element={<AppendNavigate append="reverseInvoices" />}
      />
      <Route
        path="/works/supplier/billing/:tab" // tab: reverseInvoices | disputes
        element={<PrivateRoute page={BillingPage} menuItem="billing" />}
      />

      {/* TIMECARDS */}

      <Route
        path="/works/supplier/timecards"
        element={<AppendNavigate append="submitted" />}
      />
      <Route
        path="/works/supplier/timecards/:tab" // tab: not_submitted | submitted | approved | processing | invoiced | expired | rejected | overdue | all
        element={<PrivateRoute page={TimesheetsPage} menuItem="timecards" />}
      />

      <Route
        path="/works/supplier/timecard/:id"
        element={
          <PrivateRoute page={WorkerTimesheetPage} menuItem="timecards" />
        }
      />

      {/* ACCOUNT SETTINGS */}

      <Route
        path="/works/account-settings"
        element={<AppendNavigate append="user-settings" />}
      />
      <Route
        path="/works/account-settings/:page" // page: change-password | notifications | user-settings
        element={<PrivateRoute page={AccountSettingsPage} />}
      />

      {/* REPORTS */}

      <Route
        path="/works/reports"
        element={<PrivateRoute page={ReportsPage} menuItem="reports" />}
      />

      {/**
       * [BEGIN] Legacy URLs redirecting
       * */}

      <Route
        path="/works/team/*"
        element={<NavigateWildCard to="/works/supplier/team/*" />}
      />

      <Route
        path="/works/supplier/staffing_requests/*"
        element={<NavigateWildCard to="/works/supplier/staffing-request/*" />}
      />

      <Route
        path="/works/supplier/staffing_requests/:id/candidate/:worksProfileId"
        element={
          <NavigateParsedParams to="/works/supplier/candidates/:worksProfileId/submissions" />
        }
      />

      {/**
       * [END] Legacy URLs redirecting
       * */}

      <Route path="*" element={<FourZeroFourPage />} />
    </SentryRoutes>
  );
}
